import { ChatEntry } from "@/types/chat";

import type { WorkspacePersona } from "@/config/workspacePersona";

const HUB_INITIAL_BY_PERSONA: Record<WorkspacePersona, string> = {
  leader: "How are you? Really — what's weighing on you as a leader right now?",
  district_leader: "How are you? Really — what's on your mind across the district today?",
  teacher: "How are you? Really.",
  support_staff: "How are you? Really — what's on your mind in the work you do with students and staff?",
  classified_staff: "How are you? Really — what's on your mind in the work that keeps your school running?",
};

/** First assistant message in the Hub thread when there is no history yet. */
export const getHubInitialMessage = (persona: WorkspacePersona = "teacher"): string =>
  HUB_INITIAL_BY_PERSONA[persona];

/** @deprecated Use getHubInitialMessage(persona) */
export const aiMentorInitialMessage = HUB_INITIAL_BY_PERSONA.teacher;

/** Open-ended mentor-style chats (initial transcript handling matches ai_mentor). */
export const isPrimaryChatEntry = (entry: ChatEntry): boolean =>
  entry === "ai_mentor" || entry === "get_coaching";

export const chatEntryRoutingHintMap = {
  reclaim_my_balance: "reclaim_balance",
  expand_my_impact: "expand_impact",
  reset_my_nervous_system: "daily_reset",
  connect_with_students: "conversation_students",
  strengthen_my_voice: "conversation_parents",
  lead_with_clarity: "conversation_coworkers",
  navigate_board_relationships: "conversation_board",
  navigate_parent_relationships: "conversation_parents_leader",
  leading_your_staff: "conversation_coworkers_leader",
  renew_my_spark: "renew_my_spark",
  get_coaching: "get_coaching",
  teacher_scenarios: "teacher_scenarios",
  ai_mentor: "primary_conversation", // may be empty also
} as const satisfies Record<ChatEntry, string>;

export const chatEntryTitleMap = {
  reclaim_my_balance: "Reclaim My Balance",
  expand_my_impact: "Expand My Impact",
  reset_my_nervous_system: "Reset My Nervous System",
  connect_with_students: "Connecting with Students",
  strengthen_my_voice: "Strengthen My Voice",
  lead_with_clarity: "Lead With Clarity",
  navigate_board_relationships: "Effective Board Communication",
  navigate_parent_relationships: "Navigate Parent Relationships",
  leading_your_staff: "Leading Your Staff",
  renew_my_spark: "Renew my Spark",
  get_coaching: "My Coach",
  teacher_scenarios: "Scenario Practice",
  ai_mentor: "Primary Conversation",
} as const satisfies Record<ChatEntry, string>;

export const voiceAgentIdsMap = {
  reclaim_my_balance: import.meta.env.VITE_VOICE_AGENT_RECLAIM_MY_BALANCE,
  expand_my_impact: import.meta.env.VITE_VOICE_AGENT_EXPAND_MY_IMPACT,
  reset_my_nervous_system: import.meta.env.VITE_VOICE_AGENT_RESET_NERVOUS_SYSTEM,
  connect_with_students: import.meta.env.VITE_VOICE_AGENT_CONNECT_WITH_STUDENTS_ID,
  strengthen_my_voice: import.meta.env.VITE_VOICE_AGENT_STRENGTHEN_MY_VOICE_ID,
  lead_with_clarity: import.meta.env.VITE_VOICE_AGENT_LEAD_WITH_CLARITY_ID,
  navigate_board_relationships: import.meta.env.VITE_VOICE_AGENT_NAVIGATE_BOARD_RELATIONSHIPS_ID,
  navigate_parent_relationships: import.meta.env.VITE_VOICE_AGENT_NAVIGATE_PARENT_RELATIONSHIPS_ID,
  leading_your_staff: import.meta.env.VITE_VOICE_AGENT_LEADING_YOUR_STAFF_ID,
  renew_my_spark: import.meta.env.VITE_VOICE_AGENT_RENEW_MY_SPARK_ID,
  get_coaching: import.meta.env.VITE_VOICE_AGENT_GET_COACHING_ID ?? import.meta.env.VITE_VOICE_AGENT_AI_MENTOR_ID,
  teacher_scenarios: import.meta.env.VITE_VOICE_AGENT_AI_MENTOR_ID,
  ai_mentor: import.meta.env.VITE_VOICE_AGENT_AI_MENTOR_ID,
} as const satisfies Record<ChatEntry, string>;

/** Chat entries whose ElevenLabs agents require a server-issued session token or signed URL. */
export const voiceAgentRequiresAuth = new Set<ChatEntry>([]);

export const isChatEntry = (value: unknown): value is ChatEntry =>
  typeof value === "string" && value in chatEntryRoutingHintMap;
